<template>
  <v-row class="ma-0 pa-0" align="center" justify="center">
    <v-col cols="12" class="chart-style ma-0 pa-0">
      <v-chart :id="chart_id" class="chart" :option="option" autoresize />
    </v-col>
  </v-row>
</template>

<script>
import VChart, { THEME_KEY } from "vue-echarts";
import "echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { TitleComponent, TooltipComponent, LegendComponent } from "echarts/components";

use([CanvasRenderer, TitleComponent, TooltipComponent, LegendComponent]);

export default {
  name: "ORTOptimizationComparisonChart",
  props: {
    chart_id: {
      type: String,
      default: "ort_optimization_comparison_chart"
    },
    chart_title: {
      type: String,
      default: "Original vs Optimized"
    },
    chart_data: {
      type: Array,
      default: () => []
    },
    metric_label: {
      type: String,
      default: "Impressions"
    },
    metric_type: {
      type: String,
      default: "number"
    }
  },
  components: {
    VChart
  },
  provide: {
    [THEME_KEY]: "light"
  },
  computed: {
    option() {
      const rows = Array.isArray(this.chart_data) ? this.chart_data : [];
      const labels = rows.map((row) => row.label);
      const originalValues = rows.map((row) => this.toNumber(row.original));
      const optimizedValues = rows.map((row) => this.toNumber(row.optimized));

      return {
        title: {
          text: this.chart_title,
          fontFamily: "Poppins",
          left: "center",
          top: 0
        },
        color: ["#2A9DBF", "#4EC5C1"],
        legend: {
          data: ["Original", "Optimized"],
          top: "8%",
          left: "center",
          textStyle: {
            fontFamily: "Poppins",
            fontSize: 12
          }
        },
        grid: {
          top: "19%",
          left: "3%",
          right: "5%",
          bottom: "8%",
          containLabel: true
        },
        xAxis: {
          type: "value",
          axisLabel: {
            fontSize: 12,
            fontFamily: "Poppins",
            formatter: (value) => this.formatAxisValue(value)
          },
          name: this.metric_label,
          nameLocation: "middle",
          nameGap: 30,
          nameTextStyle: {
            fontSize: 14,
            fontFamily: "Poppins",
            fontWeight: "bold"
          }
        },
        yAxis: {
          type: "category",
          data: labels,
          axisLabel: {
            fontSize: 12,
            fontFamily: "Poppins",
            interval: 0,
            width: 160,
            overflow: "truncate"
          }
        },
        tooltip: {
          order: "valueDesc",
          trigger: "axis",
          axisPointer: {
            type: "shadow"
          },
          formatter: (params) => {
            if (!params || !params.length) {
              return "";
            }

            const title = params[0].axisValueLabel || params[0].axisValue;
            const body = params
              .map((item) => {
                return `${item.marker}${item.seriesName}: ${this.formatTooltipValue(item.value)}`;
              })
              .join("<br/>");

            return `<strong>${title}</strong><br/>${body}`;
          }
        },
        series: [
          {
            name: "Original",
            type: "bar",
            data: originalValues,
            barMaxWidth: 18
          },
          {
            name: "Optimized",
            type: "bar",
            data: optimizedValues,
            barMaxWidth: 18
          }
        ]
      };
    }
  },
  methods: {
    toNumber(value) {
      const numberValue = Number(value);
      return Number.isFinite(numberValue) ? numberValue : 0;
    },
    formatAxisValue(value) {
      if (this.metric_type === "percent") {
        return `${(Number(value) * 100).toFixed(0)}%`;
      }

      if (this.metric_type === "decimal") {
        return Number(value).toFixed(1);
      }

      return Intl.NumberFormat("en", {
        notation: "compact",
        maximumFractionDigits: 1
      }).format(value);
    },
    formatTooltipValue(value) {
      if (this.metric_type === "percent") {
        return `${(Number(value) * 100).toFixed(2)}%`;
      }

      if (this.metric_type === "decimal") {
        return Number(value).toFixed(2);
      }

      return Intl.NumberFormat("en", {
        maximumFractionDigits: 0
      }).format(value);
    }
  }
};
</script>

<style scoped>
.chart-style {
  height: 360px;
  font-family: "Poppins" !important;
}
</style>
